import React, { useCallback, useState } from "react";
import { Modal } from "../../../shared-components/Modal";
import { useCreateATSIntegration } from "../../../api/ATSApi";
import { BellAlertIcon } from "@heroicons/react/24/outline";
import { useApplicationToast } from "../../../webcontent/ApplicationToastProvider";
import Button, { ButtonColorTypes } from "../../../shared-components/Button";
import FormDividedColumnContainer from "../../../shared-components/FormDividedColumnContainer";
import { ENV } from "../../../GeneralConfig";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";

interface IntegrationType {
  id: string;
  name: string;
  logo: string;
  status: "active" | "coming_soon";
}

interface ConnectIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  integration: IntegrationType;
  onSuccess: () => void;
  companyId?: string;
  onConnectionSetup: (
    message: string,
    type: "success" | "warning" | "error"
  ) => void;
}

const ConnectIntegrationModal: React.FC<ConnectIntegrationModalProps> = ({
  open,
  onClose,
  integration,
  onSuccess,
  companyId,
  onConnectionSetup,
}) => {
  const { showLargeToast } = useApplicationToast();
  const [isConfirming, setIsConfirming] = useState(false);

  const { mutate: createATSIntegration, isLoading: isCreatingIntegration } =
    useCreateATSIntegration();

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!companyId) {
        showLargeToast?.({
          title: "Error",
          message: "Company information not found",
          icon: (
            <div className="bg-red-100 text-white rounded-full p-2">
              <BellAlertIcon className="w-6" />
            </div>
          ),
        });
        return;
      }

      if (ENV === "production" && !isConfirming) {
        setIsConfirming(true);
        return;
      }

      createATSIntegration(
        {
          company_external_id: companyId,
          integration_tool: integration.id,
        },
        {
          onSuccess: (response) => {
            const link = response?.data?.link;
            if (link) {
              window.open(link, "_blank", "noopener,noreferrer");
              onConnectionSetup(
                `Connection process started for ${integration.name}. Please complete the setup in the new tab. The status will update here once completed.`,
                "success"
              );
            } else {
              onConnectionSetup(
                `Connection process started for ${integration.name}. No immediate redirect link was provided. Please check the integration status. The status will update here once completed.`,
                "warning"
              );
            }
            onSuccess();
          },
          onError: (error: any) => {
            onConnectionSetup(
              `Failed to create integration for ${integration.name}. ${
                error?.message || "Please try again."
              }`,
              "error"
            );
            onSuccess();
          },
        }
      );
    },
    [
      companyId,
      createATSIntegration,
      integration,
      onSuccess,
      showLargeToast,
      isConfirming,
      onConnectionSetup,
    ]
  );

  const handlePrimaryAction = () => {
    if (ENV === "production" && !isConfirming) {
      setIsConfirming(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <Modal open={open} width="w-1/2" onClose={onClose}>
      {isConfirming && ENV === "production" ? (
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="h-12 w-12 flex items-center justify-center">
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className="max-h-full max-w-full"
              />
            </div>
            <h2 className="text-xl font-semibold">
              Sandbox Environment Recommended
            </h2>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <p className="text-yellow-800">
              It's recommended to test integrations in the sandbox environment
              first before connecting to production. Are you sure you want to
              continue with production setup?
            </p>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              text="Use Sandbox Instead"
              colorType={ButtonColorTypes.custom}
              className="bg-white border border-gray-300 text-gray-700 rounded-md"
              onClick={onClose}
            />
            <Button
              text={
                isCreatingIntegration ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner className="h-5 w-5 mr-2" />
                    Connecting...
                  </span>
                ) : (
                  "Continue with Production"
                )
              }
              colorType={ButtonColorTypes.custom}
              className="bg-yellow-500 text-white rounded-md"
              onClick={handleSubmit}
              disabled={isCreatingIntegration}
            />
          </div>
        </div>
      ) : (
        <div>
          <FormDividedColumnContainer title={`Connect to ${integration.name}`}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center space-x-4 mb-4">
                <div className="h-12 w-12 flex items-center justify-center">
                  <img
                    src={integration.logo}
                    alt={`${integration.name} logo`}
                    className="max-h-full max-w-full"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{integration.name}</h3>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-700 mb-4">
                  Connecting to {integration.name} will allow automatic data
                  synchronization between your systems. After connecting, you'll
                  receive a magic link to complete the setup process.
                </p>

                {ENV === "production" && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
                    <p className="text-sm text-gray-600">
                      You are setting up this integration in the{" "}
                      <strong>production</strong> environment.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </FormDividedColumnContainer>

          <div className="mt-6 flex justify-end">
            <Button
              text={
                isCreatingIntegration ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner className="h-5 w-5 mr-2" />
                    Connecting...
                  </span>
                ) : (
                  "Connect"
                )
              }
              colorType={ButtonColorTypes.custom}
              className="bg-blue-600 text-white rounded-md"
              onClick={handlePrimaryAction}
              disabled={isCreatingIntegration}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConnectIntegrationModal;
